import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { mockPuzzle, mockPuzzleHinglish } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { X } from 'lucide-react';

const PuzzleOverlay = ({ isOpen, onClose, isForced = false, onSubmitAnswer = null, allowSolvedDisplay = false }) => {
  const { language, hasSolvedToday, solvePuzzle } = useAuth();
  const [selectedOption, setSelectedOption] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(language);
  const [resultBanner, setResultBanner] = useState(null);



  useEffect(() => {
    if (!isOpen) {
      setSelectedOption(null);
      setResultBanner(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const puzzle = currentLanguage === 'hinglish' ? mockPuzzleHinglish : mockPuzzle;

  const handleSubmit = async () => {
    if (!selectedOption) return;

    setIsSubmitting(true);
    setResultBanner(null);
    const submitAnswer = onSubmitAnswer || solvePuzzle;
    const result = await submitAnswer(selectedOption);
    setIsSubmitting(false);

    if (result.success) {
      const isCorrect = Boolean(result.isCorrect);
      setResultBanner({
        type: isCorrect ? 'correct' : 'incorrect',
        text: isCorrect ? 'Correct ✅' : 'Incorrect ❌',
      });

      setTimeout(() => {
        onClose();
      }, 950);
      return;
    }

    setResultBanner({
      type: 'incorrect',
      text: result.error || 'Unable to submit answer',
    });
  };

  if (hasSolvedToday && !isForced && !allowSolvedDisplay) {
    return null;
  }

  // Android 15 WebView fix: when the backdrop layer (which uses
  // `backdrop-filter: blur(...)`) sits as a sibling of the card (which is
  // `.glass-panel` with its own `backdrop-filter: blur(12px)`), Android 15's
  // Chrome/WebView has a known touch-routing regression where taps inside
  // the card get hit-tested onto the absolute backdrop sibling instead.
  // When `isForced=true` the backdrop has no onClick → those misrouted taps
  // do nothing and the user is stuck unable to tap option/submit/language
  // buttons. Fix surgically without changing logic/UI:
  //   1. Make the visual backdrop `pointer-events: none` (purely visual).
  //   2. Move the click-outside-to-close handler to the parent container
  //      with an `e.target === e.currentTarget` guard so it only fires for
  //      taps on the empty surround, never on the card or its children.
  //   3. Give the card an explicit z-10 stacking position.
  //   4. Add `touch-action: manipulation` + transparent tap-highlight to all
  //      interactive buttons so Android does not delay/swallow taps for
  //      double-tap-zoom resolution.
  const interactiveButtonStyle = {
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
  };

  const handleContainerClick = (e) => {
    if (isForced) return;
    if (e.target === e.currentTarget && typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleContainerClick}
        >
          {/* Backdrop with blur — visual only, must NOT capture touches on Android 15 */}
          <motion.div
            className="absolute inset-0 backdrop-blur-md bg-white/60"
            style={{ pointerEvents: 'none' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Puzzle Card */}
          <motion.div
            className="relative z-10 glass-panel rounded-[24px] p-6 max-w-md w-full"
            style={{ touchAction: 'manipulation' }}
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
          >
            {!isForced && (
              <button
                onClick={onClose}
                data-testid="puzzle-close-btn"
                style={interactiveButtonStyle}
                className="absolute top-4 right-4"
              >
                <X className="w-5 h-5 text-[#666666]" />
              </button>
            )}

            {/* Language Toggle */}
            <div className="flex justify-center gap-2 mb-6">
              <button
                onClick={() => setCurrentLanguage('english')}
                data-testid="language-toggle-english"
                style={interactiveButtonStyle}
                className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  currentLanguage === 'english'
                    ? 'bg-[#9333EA] text-white'
                    : 'bg-white/50 text-[#666666]'
                }`}
              >
                English
              </button>
              <button
                onClick={() => setCurrentLanguage('hinglish')}
                data-testid="language-toggle-hinglish"
                style={interactiveButtonStyle}
                className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  currentLanguage === 'hinglish'
                    ? 'bg-[#9333EA] text-white'
                    : 'bg-white/50 text-[#666666]'
                }`}
              >
                Hinglish
              </button>
            </div>

            {/* Question */}
            <h2 className="text-xl font-bold text-[#000000] mb-6 text-center font-['Outfit']">
              {puzzle.question}
            </h2>

            {/* Options */}
            <div className="space-y-3 mb-6">
              {puzzle.options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setSelectedOption(option.id)}
                  data-testid={`puzzle-option-${option.id}`}
                  style={interactiveButtonStyle}
                  className={`w-full p-4 rounded-[16px] text-left font-['DM_Sans'] ${
                    selectedOption === option.id
                      ? 'bg-[#0F52BA] text-white'
                      : 'bg-white/50 text-[#000000] hover:bg-white/70'
                  }`}
                >
                  {option.text}
                </button>
              ))}
            </div>

            {resultBanner && (
              <p className={`text-sm text-center mb-4 font-semibold font-['DM_Sans'] ${
                resultBanner.type === 'correct' ? 'text-green-600' : 'text-red-500'
              }`}>
                {resultBanner.text}
              </p>
            )}

            {/* Submit button */}
            <button
              onClick={handleSubmit}
              disabled={!selectedOption || isSubmitting}
              data-testid="puzzle-submit-btn"
              style={interactiveButtonStyle}
              className="w-full bg-gradient-to-r from-[#9333EA] via-[#EC4899] via-[#FBBF24] to-[#EF4444] text-white py-3 rounded-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Answer'}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PuzzleOverlay;
